import { useCallback, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { AutoResizer, ColumnShape } from 'react-base-table';
import isNumber from 'lodash/isNumber';
import classNames from 'classnames';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { LogEntryEdge, SystemField } from 'common/graphqlTypes';
import { DataValue } from 'plugins/types';
import { convertTimestampToMilliseconds, timezoneMoment } from 'common/util/time';
import { PARSE_TYPE, TOKEN_TYPE } from 'common/util/filter/types';
import { PlaceholderLogEntryEdge } from 'hooks/useLRQ/useEventCache';

import { SeverityIcon } from 'components/search/LogLine';
import {
  prepareHost,
  prepareLogfileName,
  prepareMessage,
  StrictLogEntry,
  StrictSettings,
} from 'components/search/logLineUtils';
import {
  useLogResultsState,
  useScrollPositioningHandler,
} from 'components/search/SearchLogsPage/LogResultsContainer.hooks';
import BaseTable, { BaseTableProps } from 'components/common/BaseTable/BaseTable';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import {
  getCellRendererState,
  getMergedSchemaFieldsState,
  viewFieldsState,
  viewState,
} from 'components/common/Managers/ViewStateManager.state';
import FacetValue from 'components/common/FacetValue/FacetValue';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';

import autosizeColumns from './autosizeColumns';

import './TableLogResults.scss';

type TableLogResultsProps = {
  logIds: string[];
};

/**
 * PureTableLogResults
 */
export function PureTableLogResults({ ...rest }: BaseTableProps<LogEntryEdge | PlaceholderLogEntryEdge>) {
  return <BaseTable {...rest} />;
}

/**
 * This component presents a tabular view of search results.
 *
 * @prop logIds    a list of continuation tokens used to look up log entries
 */
function TableLogResults(props: TableLogResultsProps) {
  const { logIds = [] } = props;
  const timezone = useRecoilValue(timezoneState);
  const settings = useRecoilValue(settingsState);
  const view = useRecoilValue(viewState);
  const columnSpecs = useRecoilValue(viewFieldsState);
  const getMergedSchemaFields = useRecoilValue(getMergedSchemaFieldsState);
  const getCellRenderer = useRecoilValue(getCellRendererState);
  const systemFields = settings[K.SETTING.SYSTEM_FIELDS];
  const userDefinedFields = settings[K.SETTING.ADDITIONAL_FIELDS];
  const {
    onItemsRendered,
    scrollOffset,
    itemData: { filter, logEntryEdges, selectedToken },
  } = useLogResultsState();
  const baseTableRef = useScrollPositioningHandler<LogEntryEdge | PlaceholderLogEntryEdge>(scrollOffset, logIds);

  // Use the label from a schema as the column header if available, and the description as the tooltip.
  const headerRenderer = ({ column }: { column: ColumnShape }) => {
    const label = columnSpecs[column.field]?.label ?? '';
    const description = columnSpecs[column.field]?.description ?? '';
    return (
      <ElementWithTooltip tooltip={description || label || column.title} placement="top">
        <span>{label || column.title}</span>
      </ElementWithTooltip>
    );
  };

  // Return custom rendered content for the given field if it has a defined renderer.
  const getRenderedCell = (field: string, rowData: LogEntryEdge | PlaceholderLogEntryEdge, value?: DataValue) => {
    const { logEntry } = rowData || {};
    const { serverInfo, threadId, timestamp, values } = logEntry || {};
    const rowValues = { ...values, ...serverInfo, threadId, timestamp };

    const Renderer = getCellRenderer(field, logEntry);
    if (Renderer) {
      return (
        <Renderer
          row={rowValues}
          fieldId={field}
          schemaFields={getMergedSchemaFields(logEntry)}
          value={value ?? rowValues[field]}
        />
      );
    }

    return null;
  };

  /**
   * Prepare columns
   */
  const tableColumns: ColumnShape<LogEntryEdge | PlaceholderLogEntryEdge>[] = useMemo(
    () => {
      const columns: ColumnShape<LogEntryEdge | PlaceholderLogEntryEdge>[] = [];
      const headerClassName = view.classNames?.tableHeader || '';
      const fieldClassName = view.classNames?.tableField || '';
      const severityFieldClassName = view.classNames?.tableSeverityField || '';
      const systemFieldClassName = view.classNames?.tableSystemField || '';

      // Icon
      columns.push({
        key: 'severity-col',
        dataKey: 'logEntry.severity',
        width: 30,
        cellRenderer: ({ cellData: severity = 0 }: { cellData: number }) => (
          <SeverityIcon classnames={severityFieldClassName} severityLevel={severity} />
        ),
      });

      // Date
      if (systemFields.includes(SystemField.date)) {
        columns.push({
          headerClassName,
          headerRenderer,
          className: classNames(fieldClassName, systemFieldClassName),
          'data-token-type': TOKEN_TYPE.FIELD,
          title: 'Date',
          key: 'date-col',
          dataKey: 'logEntry.timestamp',
          width: columnSpecs[SystemField.date]?.width || 70,
          cellRenderer: ({ rowData }: { rowData: LogEntryEdge | PlaceholderLogEntryEdge }) => {
            const timestamp = rowData.logEntry?.timestamp;
            const content = getRenderedCell(SystemField.date, rowData, timestamp);
            if (content) {
              return content;
            }
            return timestamp
              ? timezoneMoment(timezone)(convertTimestampToMilliseconds(timestamp)).format('MMM D')
              : 'Loading…';
          },
        });
      }

      // Time
      if (systemFields.includes(SystemField.timestamp)) {
        columns.push({
          headerClassName,
          headerRenderer,
          className: classNames(fieldClassName, systemFieldClassName),
          'data-token-type': TOKEN_TYPE.FIELD,
          title: 'Time',
          key: 'time-col',
          dataKey: 'logEntry.timestamp',
          width: columnSpecs[SystemField.timestamp]?.width || 120,
          cellRenderer: ({ rowData }: { rowData: LogEntryEdge | PlaceholderLogEntryEdge }) => {
            const timestamp = rowData.logEntry?.timestamp;
            const content = getRenderedCell(SystemField.timestamp, rowData, timestamp);
            if (content) {
              return content;
            }
            return timestamp
              ? timezoneMoment(timezone)(convertTimestampToMilliseconds(timestamp)).format('HH:mm:ss.SSS')
              : '';
          },
        });
      }

      // Source/Cluster
      if (systemFields.includes(SystemField.host)) {
        columns.push({
          headerClassName,
          headerRenderer,
          className: classNames(fieldClassName, systemFieldClassName),
          'data-token-type': TOKEN_TYPE.FIELD,
          title: 'Source/Cluster',
          width: columnSpecs[SystemField.host]?.width || 220, // TODO: should be calculated
          resizable: true,
          key: 'source-col',
          cellRenderer: ({ rowData }: { rowData: LogEntryEdge | PlaceholderLogEntryEdge }) => {
            const logEntry = rowData.logEntry as StrictLogEntry;
            const selector = prepareHost(logEntry);
            const host = selector?.value ?? '';
            const content = getRenderedCell(SystemField.host, rowData, host as DataValue);
            if (content) {
              return content;
            }
            return host;
          },
        });
      }

      // Logfile/Deployment
      if (systemFields.includes(SystemField.logfile)) {
        columns.push({
          headerClassName,
          headerRenderer,
          className: classNames(fieldClassName, systemFieldClassName),
          'data-token-type': TOKEN_TYPE.FIELD,
          title: 'Logfile/Deployment',
          width: columnSpecs[SystemField.logfile]?.width || 220, // TODO: should be calculated
          resizable: true,
          key: 'logfile-col',
          cellRenderer: ({ rowData }: { rowData: LogEntryEdge | PlaceholderLogEntryEdge }) => {
            const logEntry = rowData.logEntry as StrictLogEntry;
            const selector = prepareLogfileName(logEntry);
            const logfile = selector?.value ?? '';
            const content = getRenderedCell(SystemField.logfile, rowData, logfile as DataValue);
            if (content) {
              return content;
            }
            return logfile;
          },
        });
      }

      // User-defined fields
      let userDefinedColumns: ColumnShape<LogEntryEdge | PlaceholderLogEntryEdge>[] = userDefinedFields.map(
        (field: string) => ({
          headerClassName,
          headerRenderer,
          className: fieldClassName,
          'data-token-type': PARSE_TYPE.TERM,
          title: columnSpecs[field]?.label ?? field,
          width: columnSpecs[field]?.width ?? 0, // handled by `autosizeColumns` when zero
          key: `userDefinedField-${field}-col`,
          field,
          resizable: true,
          cellRenderer: ({ rowData }: { rowData: LogEntryEdge | PlaceholderLogEntryEdge }) => {
            const { continuationToken, logEntry } = rowData || {};
            const { facetsWithTruncatedValues, serverInfo, threadId, values } = logEntry || {};
            const rowValues = { ...values, ...serverInfo, threadId };
            const termValue = rowValues[field];

            // TODO: tell the renderer if the value was truncated
            const content = getRenderedCell(field, rowData);
            if (content) {
              return content;
            }

            const isNumeric = isNumber(termValue);
            const isTruncated = facetsWithTruncatedValues?.includes(field);

            return (
              <span data-term-field={field} data-term-value-type={isNumeric ? TOKEN_TYPE.NUMBER : TOKEN_TYPE.STRING}>
                <FacetValue
                  field={field}
                  value={String(termValue ?? '')}
                  filter={filter}
                  settings={settings}
                  doHighlight
                  doLinkify
                  isTruncated={isTruncated}
                  continuationToken={continuationToken}
                />
              </span>
            );
          },
        }),
      );

      userDefinedColumns = autosizeColumns(userDefinedColumns, logEntryEdges);
      columns.push(...userDefinedColumns);

      // Raw Log (message)
      if (systemFields.includes(SystemField.rawLogLine)) {
        columns.push({
          headerClassName,
          headerRenderer,
          className: fieldClassName,
          'data-token-type': TOKEN_TYPE.UNKNOWN,
          title: 'Raw Log',
          key: 'rawLog',
          dataKey: 'logEntry.values.message',
          width: columnSpecs[SystemField.rawLogLine]?.width || 800, // TODO: should be calculated
          resizable: true,
          cellRenderer: ({ rowData }: { rowData: LogEntryEdge | PlaceholderLogEntryEdge }) => {
            const logEntry = rowData.logEntry as StrictLogEntry;
            const selector = prepareMessage(logEntry, settings as StrictSettings);
            const message = selector?.value ?? '';
            // TODO: tell the renderer if the message was truncated
            const content = getRenderedCell(SystemField.rawLogLine, rowData, message as DataValue);
            if (content) {
              return content;
            }

            const { continuationToken } = rowData || {};
            const isTruncated = logEntry?.facetsWithTruncatedValues?.includes(K.FIELD.MESSAGE);

            return (
              <div>
                <FacetValue
                  field={K.FIELD.MESSAGE}
                  value={message}
                  filter={filter}
                  settings={settings}
                  doHighlight
                  doLinkify
                  isTruncated={isTruncated}
                  continuationToken={continuationToken}
                />
              </div>
            );
          },
        });
      }

      return columns;
    },
    // optimization: We don't want to track logEntryEdges here to do not re-measure the columns on scroll
    [filter, settings, systemFields, timezone, userDefinedFields],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Action Handlers
   */
  const onRowsRendered = useCallback(
    ({ startIndex, stopIndex }) => onItemsRendered({ visibleStartIndex: startIndex, visibleStopIndex: stopIndex }),
    [onItemsRendered],
  );
  const rowRenderer = useCallback(
    ({ cells, rowData }) =>
      rowData?.logEntry ? cells : <div className="TableLogResults__LoadingPlaceholder">Loading…</div>,
    [],
  );
  const rowProps = useCallback(
    ({ rowData, rowIndex }) => ({
      'data-continuation-token': rowData?.continuationToken || String(rowIndex),
      'data-test-id': ELEMENT.searchLogsLogLine,
    }),
    [],
  );
  const cellProps = useCallback(
    ({ column }) => ({ 'data-token-type': column['data-token-type'] || TOKEN_TYPE.UNKNOWN }),
    [],
  );
  const rowClassName = useCallback(
    ({ rowData }) => (rowData?.continuationToken === selectedToken ? 'TableLogResults__Row--Active' : ''),
    [selectedToken],
  );
  const tableClassName = useMemo(
    () =>
      classNames(
        settings.fontFamily === K.FONT_FAMILY.MONOSPACE
          ? 'TableLogResults'
          : 'TableLogResults TableLogResults--RegularFont',
        view.classNames?.table,
      ),
    [settings.fontFamily, view],
  );

  // AutoResizer measures the size of the parent
  //  container and supplies width, height to the table to size itself correctly
  return (
    <AutoResizer>
      {({ width, height }) => (
        <PureTableLogResults
          forwardedRef={baseTableRef}
          className={tableClassName}
          fixed
          width={width}
          height={height}
          ignoreFunctionInColumnCompare={false}
          data={logEntryEdges}
          columns={tableColumns}
          rowKey="continuationToken"
          rowProps={rowProps}
          cellProps={cellProps}
          rowClassName={rowClassName}
          estimatedRowHeight={31}
          onRowsRendered={onRowsRendered}
          rowRenderer={rowRenderer}
        />
      )}
    </AutoResizer>
  );
}

export default TableLogResults;
